<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { listPathOf } from 'dashboard/helper/conversationListPath';
import { useS3Kanban } from 'dashboard/composables/useS3Kanban';

// Smith3D alert bar (owner 2026-09-22, replaces the floating bubble): sits with
// the chat-list search box — the one place staff eyes pass on every filtered
// view — and is sized entirely by the embedded page via boardHeight messages
// (0 = hidden until the next alert; 40 = ticker; taller = expanded panel).
// All bar behaviour (hide-until-new, severity colors, marquee, SSE push) lives
// server-side in mk-backend-s3 so iterating on it needs no Chatwoot rebuild.
// Gated by smith3d TEAM membership, independent of the current view.
const S3_BOARD_ORIGIN =
  window.location.hostname === 'localhost'
    ? 'http://localhost:3669'
    : 'https://sapi3.mkhb.my';

const route = useRoute();
const router = useRouter();
const teams = useMapGetter('teams/getMyTeams');
const currentUser = useMapGetter('getCurrentUser');
const { showKanbanPage } = useS3Kanban();

const isSmithStaff = computed(() =>
  teams.value.some(team => team.name.toLowerCase() === 'smith3d')
);

const barHeight = ref(0);
const baseHeight = ref(0);
const availableHeight = ref(0);
const rootRef = ref(null);
const frameRef = ref(null);

// Who is looking powers the board's "seen by" markers.
const barSrc = computed(() => {
  const theme = document.body.classList.contains('dark') ? 'dark' : 'light';
  const { id, display_name: displayName, name } = currentUser.value || {};
  const who = (displayName || name || '').replace(/\s*\(.*\)\s*$/, '').trim();
  return `${S3_BOARD_ORIGIN}/api/store-dashboard/bar?theme=${theme}&uid=${id || ''}&who=${encodeURIComponent(who)}`;
});

// The opened board grows down the chat-list column (pushing the list) but
// always leaves the search box and the newest chats visible, so an incoming
// chat is never hidden behind it. The page inside sizes its list to fit.
const VISIBLE_CHATS = 2;
const measureAvailable = () => {
  const el = rootRef.value;
  const column = el?.closest('.conversations-list-wrap');
  const list = column?.querySelector('.conversations-list');
  if (!list) return;
  const listRect = list.getBoundingClientRect();
  const cards = list.querySelectorAll('.conversation');
  const lastKept = cards[Math.min(VISIBLE_CHATS, cards.length) - 1];
  const keptChats = lastKept
    ? lastKept.getBoundingClientRect().bottom - listRect.top + list.scrollTop
    : 0;
  const barRect = el.getBoundingClientRect();
  const between = listRect.top - barRect.bottom;
  const px = Math.floor(
    column.getBoundingClientRect().bottom - barRect.top - between - keptChats
  );
  availableHeight.value = Math.max(0, px);
  frameRef.value?.contentWindow?.postMessage(
    { event: 'boardViewport', px: availableHeight.value },
    S3_BOARD_ORIGIN
  );
};

// Highlights the open chat's row so staff see where they are on the board.
const postActiveChat = () => {
  frameRef.value?.contentWindow?.postMessage(
    {
      event: 'boardActiveChat',
      conversationId: Number(route.params.conversation_id) || 0,
    },
    S3_BOARD_ORIGIN
  );
};
watch(() => route.params.conversation_id, postActiveChat);

// The page is often still settling when the frame loads; measure again once.
const onFrameLoad = () => {
  measureAvailable();
  postActiveChat();
  setTimeout(measureAvailable, 1000);
};

// Never squeeze the always-visible strip itself, even on a very short screen.
const frameHeight = computed(() =>
  availableHeight.value
    ? Math.max(
        baseHeight.value,
        Math.min(barHeight.value, availableHeight.value)
      )
    : barHeight.value
);

const onBoardMessage = event => {
  if (event.origin !== S3_BOARD_ORIGIN) return;
  let data = event.data;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return;
    }
  }
  if (data?.event === 'boardHeight') {
    barHeight.value = Number(data.px) || 0;
    baseHeight.value = Number(data.base ?? data.px) || 0;
    measureAvailable();
    return;
  }
  // Kanban shortcut: close the open chat but stay on this list — the empty
  // chat pane shows the print-job kanban.
  if (data?.event === 'boardShowKanban') {
    router.push(listPathOf(route.path));
    if (typeof data.kanban === 'string' && data.kanban.startsWith('/')) {
      showKanbanPage(data.kanban);
    }
    return;
  }
  // Label-count chips navigate the SPA in place (e.g. /app/.../label/x).
  // A chip may also name a kanban page (e.g. /print-jobs, /preorders): the
  // list is filtered AND the kanban beside it switches to that page.
  if (data?.event === 'boardGoto') {
    if (typeof data.path === 'string' && data.path.startsWith('/app/')) {
      router.push(data.path);
    }
    if (typeof data.kanban === 'string' && data.kanban.startsWith('/')) {
      showKanbanPage(data.kanban);
    }
    return;
  }
  if (data?.event !== 'boardNavigate' || !data.conversationId) return;
  router.push({
    name: 'conversations_through_company',
    params: {
      accountId: route.params.accountId,
      company: 's3',
      conversation_id: data.conversationId,
    },
  });
};

// Banners and late-loading header rows change the column after load, so
// re-measure whenever the column itself resizes, not only on window resize.
const columnObserver = new ResizeObserver(measureAvailable);
watch(rootRef, el => {
  columnObserver.disconnect();
  const column = el?.closest('.conversations-list-wrap');
  if (column) columnObserver.observe(column);
});

onMounted(() => {
  window.addEventListener('message', onBoardMessage);
  window.addEventListener('resize', measureAvailable);
});
onBeforeUnmount(() => {
  window.removeEventListener('message', onBoardMessage);
  window.removeEventListener('resize', measureAvailable);
  columnObserver.disconnect();
});
</script>

<template>
  <div
    v-if="isSmithStaff"
    ref="rootRef"
    class="w-full overflow-hidden transition-[height] duration-300 shrink-0"
    :style="{ height: `${frameHeight}px` }"
  >
    <iframe
      ref="frameRef"
      :src="barSrc"
      class="w-full h-full border-0"
      :title="$t('CHAT_LIST.S3_BOARD')"
      @load="onFrameLoad"
    />
  </div>
</template>
